const MAX_DIMENSION = 1920;
const TARGET_BYTES = 250 * 1024; // 250 KB

export async function compressImage(file: File): Promise<File> {
  const bitmap = await createImageBitmap(file);

  let { width, height } = bitmap;
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const scale = MAX_DIMENSION / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  // Try decreasing quality first, then shrink dimensions if still too large
  for (const quality of [0.85, 0.75, 0.65, 0.5, 0.4]) {
    const blob = await toBlob(canvas, "image/jpeg", quality);
    if (blob.size <= TARGET_BYTES) {
      return new File([blob], toJpegName(file.name), { type: "image/jpeg" });
    }
  }

  // Still too large — halve dimensions repeatedly until it fits
  let scale = 0.5;
  while (scale >= 0.125) {
    const scaled = document.createElement("canvas");
    scaled.width = Math.round(width * scale);
    scaled.height = Math.round(height * scale);
    scaled.getContext("2d")!.drawImage(canvas, 0, 0, scaled.width, scaled.height);
    const blob = await toBlob(scaled, "image/jpeg", 0.7);
    if (blob.size <= TARGET_BYTES) {
      return new File([blob], toJpegName(file.name), { type: "image/jpeg" });
    }
    scale /= 2;
  }

  // Absolute last resort
  const blob = await toBlob(canvas, "image/jpeg", 0.3);
  return new File([blob], toJpegName(file.name), { type: "image/jpeg" });
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Canvas toBlob failed"))),
      type,
      quality,
    ),
  );
}

function toJpegName(name: string) {
  return name.replace(/\.[^.]+$/, "") + ".jpg";
}
